/**
 * Where an answer is given, and therefore which key it is stored under.
 *
 * The vocabulary the whole configurator is keyed by, in a module of its own:
 * the key format reads it and the tree walk writes it, and each of those needs
 * what the other exports.
 *
 * A room carries its storey so the chain from a room up to the property can be
 * walked without a lookup table — inheritance is read on every price, every
 * card and every position.
 */
export type SelectionScope =
  | { level: 'OBJECT' }
  | { level: 'FLOOR'; floorId: number }
  | { level: 'ROOM'; roomId: number; floorId: number | null }

export const OBJECT_SCOPE: SelectionScope = { level: 'OBJECT' }

export const floorScope = (floorId: number): SelectionScope => ({
  level: 'FLOOR',
  floorId,
})

export const roomScope = (
  roomId: number,
  floorId: number | null,
): SelectionScope => ({ level: 'ROOM', roomId, floorId })

/** Which storey each room stands on — what makes the answer chain walkable: a
 *  room reads its own pick, then its storey's, then the property's. */
export const floorOfRooms = (
  rooms: readonly { id: number; floorId?: number | null }[] | null | undefined,
): Map<number, number | null> =>
  new Map((rooms ?? []).map(room => [room.id, room.floorId ?? null]))

/**
 * The place a formula is evaluated at for a room, or the property when there is
 * none — what a caller holding a nullable room id names its place with.
 *
 * The storey is left out: the context factory holds the room list and fills it
 * in, so a caller cannot name a room as standing on the wrong one.
 */
export const placeOfRoom = (roomId: number | null): SelectionScope =>
  roomId == null ? OBJECT_SCOPE : roomScope(roomId, null)

/** The other direction, as the flat pair a position records: the room, or the
 *  storey where there is none. `positionPlaceOf` drops the storey beside a
 *  room, which is the half this cannot state for itself. */
export const rawPlaceOf = (
  scope: SelectionScope,
): { roomId: number | null; floorId: number | null } => ({
  roomId: scope.level === 'ROOM' ? scope.roomId : null,
  floorId: scope.level === 'OBJECT' ? null : scope.floorId,
})

/**
 * The inverse of `rawPlaceOf`: the place a position records, read back as the
 * scope it was booked at.
 *
 * A room names itself and the storey it stands on; a position with only a
 * storey was booked at that storey; one with neither was booked at the object.
 */
export const scopeOfPlace = (place: {
  roomId: number | null
  floorId: number | null
}): SelectionScope =>
  place.roomId != null
    ? roomScope(place.roomId, place.floorId)
    : place.floorId != null
      ? floorScope(place.floorId)
      : OBJECT_SCOPE

/**
 * How a place is written: nothing for the object, `f<id>` for a storey, the
 * bare id for a room.
 *
 * The one spelling of a place. A stored selection key is a question id and
 * this, a per-place cache is keyed by this, and the formula editor remembers
 * the place it was testing as this — so none of them can drift from the
 * format the persisted blob is in.
 */
export const scopeKey = (scope: SelectionScope): string =>
  scope.level === 'OBJECT'
    ? ''
    : scope.level === 'FLOOR'
      ? `${FLOOR_PREFIX}${scope.floorId}`
      : `${scope.roomId}`

const FLOOR_PREFIX = 'f'

/** The place a key names, or null where it names none — a blob that has been
 *  corrupted, or a value stored before the format was what it is. */
export const parseScopeKey = (key: string): SelectionScope | null => {
  if (key === '') return OBJECT_SCOPE
  const isPositiveInt = (n: number) => Number.isInteger(n) && n > 0
  if (key.startsWith(FLOOR_PREFIX)) {
    const floorId = Number(key.slice(FLOOR_PREFIX.length))
    return isPositiveInt(floorId) ? floorScope(floorId) : null
  }
  const roomId = Number(key)
  // A room key carries no storey: which storey a room stands on is a property
  // of the room, and the caller that needs the chain reads it off its rooms.
  return isPositiveInt(roomId) ? roomScope(roomId, null) : null
}

/**
 * Wraps a per-place build so each place is built once and kept.
 *
 * Every consumer of a formula context needs this: building one walks every
 * house-element row and every option in the tree, and the surfaces reading it
 * — the cards, the position marks, the summary, the energy labels — ask for the
 * same handful of places over and over. The cache lives and dies with the
 * wrapper, so a new tree or a new answer cannot be served from a stale one.
 */
export function cacheByPlace<T>(
  build: (focus: SelectionScope) => T,
): (focus: SelectionScope) => T {
  const cache = new Map<string, T>()
  return focus => {
    const key = scopeKey(focus)
    if (!cache.has(key)) cache.set(key, build(focus))
    return cache.get(key)!
  }
}

/** The scope and every scope above it, deepest first — the order an answer is
 *  looked up in, so the nearest one given wins. */
export function scopeChain(scope: SelectionScope): SelectionScope[] {
  if (scope.level === 'OBJECT') return [OBJECT_SCOPE]
  if (scope.level === 'FLOOR') return [scope, OBJECT_SCOPE]
  return scope.floorId == null
    ? [scope, OBJECT_SCOPE]
    : [scope, floorScope(scope.floorId), OBJECT_SCOPE]
}

export const sameScope = (a: SelectionScope, b: SelectionScope): boolean =>
  a.level === b.level &&
  (a.level !== 'FLOOR' || a.floorId === (b as typeof a).floorId) &&
  (a.level !== 'ROOM' || a.roomId === (b as typeof a).roomId)
